/**
 * Staff dashboard: today at a glance (meetings, new leads, AI proposals), my tasks, client
 * submissions to review, long-outstanding client requests, and open work by service.
 */
import { MODULE_LABELS, MODULES, type Module } from "@clientflow/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, CalendarClock, Check, Inbox, Sparkles } from "lucide-react";
import { useState, type ComponentType } from "react";
import { Link } from "wouter";
import { Empty, Loading, PageHeader, Pager, Section, StatRow, usePaged } from "@/components/page";
import { RequestStatus } from "@/components/panels";
import { api } from "@/lib/api";
import { daysBetween, formatDate, plural } from "@/lib/format";
import type { ClientRequest, Task } from "@/lib/types";
import { SERVICE_PATHS } from "@/lib/types";
import type { Me } from "@/lib/session";
import { TableScroll } from "@/components/table-scroll";

type MyDay = {
  today: string;
  myTasks: Task[];
  toReview: ClientRequest[];
  waitingOnClients: ClientRequest[];
  pipeline: Array<{ module: Module; stageKey: string; count: number }>;
  /** How many there are in all; each list above holds the first 500 at most. */
  totals: { myTasks: number; overdueTasks: number; toReview: number; waitingOnClients: number };
};

/** Under a paged list: says so when the server sent only the first part of a longer list. */
function Capped({ shown, total, noun, order }: { shown: number; total: number; noun: string; order: string }) {
  if (total <= shown) return null;
  return (
    <p className="pt-2 text-xs text-muted-foreground">
      Showing the first {shown.toLocaleString("en-US")} of {total.toLocaleString("en-US")} {noun}, {order}. The rest appear here as these are cleared.
    </p>
  );
}

function TaskList({ tasks, onDone }: { tasks: Task[]; onDone: (id: string) => void }) {
  const page = usePaged(tasks);
  return (
    <>
      <ul className="divide-y divide-border">
        {page.items.map((t) => (
          <li key={t.id} className="flex items-start gap-3 py-2.5 sm:items-center">
            <button
              aria-label={`Mark "${t.title}" complete`}
              title="Mark complete"
              onClick={() => onDone(t.id)}
              className="group mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded border sm:mt-0 border-input hover:border-primary focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-primary"
            >
              <Check className="h-3.5 w-3.5 opacity-0 group-hover:opacity-60 group-focus-visible:opacity-60" />
            </button>
            <div className="min-w-0 flex-1">
              <p className="text-sm sm:truncate">{t.title}</p>
              {t.clientId && (
                <Link href={`/clients/${t.clientId}`} className="text-xs text-muted-foreground hover:underline">
                  {t.clientName}
                </Link>
              )}
              {t.dueDate && (
                <p className={`text-xs tabular sm:hidden ${t.overdue ? "font-semibold text-destructive" : "text-muted-foreground"}`}>
                  {t.overdue ? "Past due · " : "Due "}
                  {formatDate(t.dueDate)}
                </p>
              )}
            </div>
            <span className={`hidden whitespace-nowrap text-xs tabular sm:inline ${t.overdue ? "font-semibold text-destructive" : "text-muted-foreground"}`}>
              {t.dueDate ? `${t.overdue ? "Past due · " : "Due "}${formatDate(t.dueDate)}` : ""}
            </span>
          </li>
        ))}
      </ul>
      <Pager paged={page} noun="tasks" compact />
    </>
  );
}

function ReviewList({ requests }: { requests: ClientRequest[] }) {
  const page = usePaged(requests);
  return (
    <>
      <ul className="divide-y divide-border">
        {page.items.map((r) => (
          <li key={r.id} className="flex items-center gap-3 py-2.5">
            <div className="min-w-0 flex-1">
              <Link href={`/clients/${r.clientId}?tab=requests`} className="block text-sm hover:underline sm:truncate">
                {r.title}
              </Link>
              <p className="text-xs text-muted-foreground">{r.clientName}</p>
            </div>
            <RequestStatus status={r.status} />
          </li>
        ))}
      </ul>
      <Pager paged={page} noun="submissions" compact />
    </>
  );
}

type WaitingGroup = { clientId: string; clientName: string; items: ClientRequest[]; oldest: string };

function WaitingTable({ groups, today }: { groups: WaitingGroup[]; today: string }) {
  const page = usePaged(groups);
  return (
    <>
      <TableScroll label="Waiting on clients table">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-muted-foreground">
            <tr className="border-b border-border">
              <th className="py-2 pr-4 font-medium">Client</th>
              <th className="py-2 pr-4 font-medium">Outstanding items</th>
              <th className="py-2 pr-4 font-medium">
                Oldest<span className="hidden sm:inline"> request</span>
              </th>
              <th className="hidden py-2 font-medium sm:table-cell">Reminders sent</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {page.items.map((g) => (
              <tr key={g.clientId}>
                <td className="py-2.5 pr-4 align-top">
                  <Link href={`/clients/${g.clientId}?tab=requests`} className="font-medium hover:underline">
                    {g.clientName}
                  </Link>
                </td>
                <td className="py-2.5 pr-4">
                  <p className="tabular">
                    {g.items.length} item{g.items.length > 1 ? "s" : ""}
                  </p>
                  <p className="line-clamp-2 max-w-xl text-xs sm:line-clamp-1 text-muted-foreground" title={g.items.map((r) => r.title).join("\n")}>
                    {g.items.map((r) => r.title).join(" · ")}
                  </p>
                </td>
                <td className="whitespace-nowrap py-2.5 pr-4 align-top tabular">
                  {formatDate(g.oldest)}
                  <span className="block text-xs text-muted-foreground">{plural(daysBetween(g.oldest.slice(0, 10), today), "day")} ago</span>
                </td>
                <td className="hidden py-2.5 align-top tabular sm:table-cell">{g.items.reduce((a, r) => a + r.remindersSent, 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroll>
      <Pager paged={page} noun="clients" compact />
    </>
  );
}

function Glance({ href, icon: Icon, label, value, detail }: { href: string; icon: ComponentType<{ className?: string }>; label: string; value: number; detail: string }) {
  return (
    <Link href={href} className="group flex items-center gap-3 rounded-lg border border-card-border bg-card px-4 py-3 transition hover:border-primary/40">
      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${value ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>
        <Icon className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium">
          <span className="tabular">{value}</span> {label}
        </span>
        <span className="block truncate text-xs text-muted-foreground">{detail}</span>
      </span>
      <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition group-hover:text-primary" />
    </Link>
  );
}

/** A glance tile's box and lines, empty, while its figure loads. */
function GlancePlaceholder() {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-card-border bg-card px-4 py-3" aria-hidden>
      <span className="h-9 w-9 shrink-0 rounded-full bg-muted" />
      <span className="min-w-0 flex-1">
        <span className="block text-sm">&nbsp;</span>
        <span className="block text-xs">&nbsp;</span>
      </span>
    </div>
  );
}

type Meeting = { id: string; status: string; startsAt: string; typeName: string; clientName: string | null; contactName: string };

/** Today at a glance: my meetings, new leads, AI proposals waiting for a person. Each tile hides itself when its data isn't available to this role. */
function TodayStrip({ today }: { today: string }) {
  const { data: meetings, isLoading: l1 } = useQuery<{ timeZone: string; rows: Meeting[] }>({ queryKey: [`/api/bookings?mine=1&from=${today}&to=${today}`] });
  const { data: leads, isLoading: l2 } = useQuery<{ counts: Partial<Record<string, number>> }>({ queryKey: ["/api/leads?view=open"] });
  const { data: ai, isLoading: l3 } = useQuery<{ pending: number }>({ queryKey: ["/api/ai/proposals/count"] });
  const now = new Date().toISOString();
  const upcoming = (meetings?.rows ?? []).filter((m) => m.status !== "cancelled");
  const next = upcoming.find((m) => m.startsAt > now);
  const time = (iso: string) => new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: meetings?.timeZone });
  const newLeads = leads?.counts.new ?? 0;
  if (!meetings && !leads && !ai && !l1 && !l2 && !l3) return null;
  // A tile that is still loading holds its place, so the figures and lists below do not move when it arrives.
  return (
    <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {l1 && <GlancePlaceholder />}
      {meetings && (
        <Glance
          href="/meetings"
          icon={CalendarClock}
          label={`meeting${upcoming.length === 1 ? "" : "s"} today`}
          value={upcoming.length}
          detail={next ? `Next: ${time(next.startsAt)} · ${next.clientName ?? next.contactName} · ${next.typeName}` : upcoming.length ? "All of today's meetings have started." : "Nothing on your calendar today."}
        />
      )}
      {l2 && <GlancePlaceholder />}
      {leads && <Glance href="/clients/leads" icon={Inbox} label={`new lead${newLeads === 1 ? "" : "s"}`} value={newLeads} detail={newLeads ? "Not contacted yet." : "No new inquiries waiting."} />}
      {l3 && <GlancePlaceholder />}
      {ai && <Glance href="/ai" icon={Sparkles} label={`AI proposal${ai.pending === 1 ? "" : "s"} to review`} value={ai.pending} detail={ai.pending ? "Nothing runs until a person approves it." : "Nothing waiting for approval."} />}
    </div>
  );
}

function longDate(iso: string) {
  return new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
}

export function MyDayPage({ me }: { me: Me }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<MyDay>({ queryKey: ["/api/my-day"] });
  // The figures above the lists double as a switch: one list at a time, or (none chosen) all of them.
  const [focus, setFocus] = useState<"tasks" | "overdue" | "review" | "waiting" | null>(null);
  const toggle = (f: NonNullable<typeof focus>) => () => setFocus((cur) => (cur === f ? null : f));
  const done = useMutation({
    mutationFn: (id: string) => api.send("PATCH", `/api/tasks/${id}`, { status: "done" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/my-day"] }),
  });
  if (isLoading || !data) return <Loading />;

  const totals = new Map<Module, number>();
  for (const p of data.pipeline) totals.set(p.module, (totals.get(p.module) ?? 0) + p.count);
  const overdueTasks = data.totals.overdueTasks;

  // Outstanding requests grouped by client, oldest first, so one client's six items read as one line.
  const byClient = new Map<string, { clientId: string; clientName: string; items: ClientRequest[] }>();
  for (const r of data.waitingOnClients) {
    const g = byClient.get(r.clientId) ?? { clientId: r.clientId, clientName: r.clientName ?? "Client", items: [] };
    g.items.push(r);
    byClient.set(r.clientId, g);
  }
  const groups = [...byClient.values()]
    .map((g) => ({ ...g, oldest: g.items.reduce((a, r) => (r.createdAt < a ? r.createdAt : a), g.items[0]!.createdAt) }))
    .sort((a, b) => a.oldest.localeCompare(b.oldest));

  return (
    <>
      <PageHeader title="Dashboard" description={`${me.user?.name} · ${longDate(data.today)}`} />

      <TodayStrip today={data.today} />

      <StatRow
        stats={[
          { label: "My open tasks", value: data.totals.myTasks, onClick: toggle("tasks"), selected: focus === "tasks" },
          { label: "My past-due tasks", value: overdueTasks, tone: overdueTasks ? "danger" : "default", onClick: toggle("overdue"), selected: focus === "overdue" },
          { label: "Client submissions to review", value: data.totals.toReview, tone: data.totals.toReview ? "warning" : "default", onClick: toggle("review"), selected: focus === "review" },
          { label: "Requests open 21+ days", value: data.totals.waitingOnClients, tone: data.totals.waitingOnClients ? "warning" : "default", onClick: toggle("waiting"), selected: focus === "waiting" },
        ]}
      />

      {totals.size > 0 && (
        <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {MODULES.filter((m) => totals.has(m)).map((m) => (
            <Link key={m} href={SERVICE_PATHS[m].workflow} className="group flex items-center justify-between rounded-lg border border-card-border bg-card px-4 py-3 transition hover:border-primary/40">
              <span>
                <span className="block text-sm font-medium">{MODULE_LABELS[m]}</span>
                <span className="text-xs text-muted-foreground">
                  <span className="tabular">{totals.get(m)}</span> open engagement{totals.get(m) === 1 ? "" : "s"}
                </span>
              </span>
              <ArrowRight className="h-4 w-4 text-muted-foreground transition group-hover:text-primary" />
            </Link>
          ))}
        </div>
      )}

      {focus && (
        <p className="mb-3 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          Showing one list.
          <button type="button" onClick={() => setFocus(null)} className="font-medium text-primary hover:underline">
            Show all
          </button>
        </p>
      )}
      <div className="grid gap-6 lg:grid-cols-2">
        {(focus === null || focus === "tasks" || focus === "overdue") && (
          <Section title={focus === "overdue" ? "My past-due tasks" : "My tasks"} className={focus ? "lg:col-span-2" : undefined}>
            {(focus === "overdue" ? overdueTasks : data.myTasks.length) === 0 ? (
              <Empty>{focus === "overdue" ? "None of your tasks are past due." : "No tasks are assigned to you."}</Empty>
            ) : (
              <>
                <TaskList tasks={data.myTasks.filter((t) => focus !== "overdue" || t.overdue)} onDone={(id) => done.mutate(id)} />
                <Capped shown={focus === "overdue" ? data.myTasks.filter((t) => t.overdue).length : data.myTasks.length} total={focus === "overdue" ? overdueTasks : data.totals.myTasks} noun="tasks" order="earliest due first" />
              </>
            )}
          </Section>
        )}

        {(focus === null || focus === "review") && (
          <Section title="Client submissions to review" className={focus ? "lg:col-span-2" : undefined}>
            {data.toReview.length === 0 ? (
              <Empty>No client submissions are awaiting review.</Empty>
            ) : (
              <>
                <ReviewList requests={data.toReview} />
                <Capped shown={data.toReview.length} total={data.totals.toReview} noun="submissions" order="oldest first" />
              </>
            )}
          </Section>
        )}

        {(focus === null || focus === "waiting") && (
          <Section title="Client requests outstanding over 21 days" description="Grouped by client, oldest first." className="lg:col-span-2">
            {groups.length === 0 ? (
              <Empty>No client requests are outstanding beyond 21 days.</Empty>
            ) : (
              <>
                <WaitingTable groups={groups} today={data.today} />
                <Capped shown={data.waitingOnClients.length} total={data.totals.waitingOnClients} noun="requests" order="oldest first" />
              </>
            )}
          </Section>
        )}
      </div>
    </>
  );
}
